import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/services/authApi'
import type { UserInfo } from '@/models/Auth'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const user = ref<UserInfo | null>(null)
  const appName = ref('Fullstack Template')
  const landingPageEnabled = ref(true)
  const debugWidgetEnabled = ref(false)
  const authEnabled = ref(true)
  const credentialsAuthEnabled = ref(true)
  const keycloakAuthEnabled = ref(false)
  const oidcAutoRedirect = ref(false)
  const setupRequired = ref(false)
  const userRegistrationEnabled = ref(true)
  const emailVerificationEnabled = ref(false)
  const authConfigLastFetched = ref<number | null>(null)

  // Cache TTL for auth config (5 minutes)
  const AUTH_CONFIG_CACHE_TTL = 5 * 60 * 1000

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => {
    return user.value?.roles?.some(role => role.toLowerCase() === 'admin') ?? false
  })
  
  // Determine the primary auth type based on configuration
  const authType = computed(() => {
    if (credentialsAuthEnabled.value && keycloakAuthEnabled.value) {
      return 'mixed'
    } else if (keycloakAuthEnabled.value) {
      return 'keycloak'
    } else if (credentialsAuthEnabled.value) {
      return 'credentials'
    } else {
      return 'external'
    }
  })

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ username: email, password })
      token.value = response.data.access_token
      localStorage.setItem('token', token.value!)
      // Store refresh token if provided
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token)
      }
      
      // Fetch user info
      await fetchUserInfo()
      
      return true
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  const logout = () => {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refresh_token')
    // Invalidate auth config cache on logout
    authConfigLastFetched.value = null
  }

  const setTokens = (accessToken: string, refreshToken?: string, expiresIn?: number) => {
    token.value = accessToken
    localStorage.setItem('token', accessToken)
    
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken)
    }
    
    // Optionally set expiration time
    if (expiresIn) {
      const expiresAt = Date.now() + (expiresIn * 1000)
      localStorage.setItem('token_expires_at', expiresAt.toString())
    }
  }

  const invalidateAuthConfig = () => {
    authConfigLastFetched.value = null
  }

  const fetchUserInfo = async () => {
    if (!token.value) return
    
    try {
      const response = await authApi.getCurrentUser()
      user.value = response.data
    } catch (error) {
      console.error('Failed to fetch user info:', error)
      logout()
    }
  }

  const refreshUserInfo = async () => {
    await fetchUserInfo()
  }

  const checkAuthConfig = async (forceRefresh = false) => {
    // Check if we have cached data and it's still valid
    const now = Date.now()
    const isCacheValid = authConfigLastFetched.value && 
                        (now - authConfigLastFetched.value) < AUTH_CONFIG_CACHE_TTL

    if (!forceRefresh && isCacheValid) {
      return // Use cached data
    }

    try {
      const response = await authApi.getAuthConfig()
      appName.value = response.data.app_name
      landingPageEnabled.value = response.data.landing_page_enabled ?? true
      debugWidgetEnabled.value = response.data.debug_widget_enabled ?? false
      authEnabled.value = response.data.auth_enabled
      credentialsAuthEnabled.value = response.data.credentials_auth_enabled ?? true
      keycloakAuthEnabled.value = response.data.keycloak_auth_enabled ?? false
      oidcAutoRedirect.value = response.data.oidc_auto_redirect ?? false
      setupRequired.value = response.data.setup_required
      userRegistrationEnabled.value = response.data.user_registration_enabled
      emailVerificationEnabled.value = response.data.email_verification_enabled
      authConfigLastFetched.value = now
    } catch (error) {
      console.error('Failed to fetch auth config:', error)
    }
  }

  // Initialize store
  if (token.value) {
    fetchUserInfo()
  }

  return {
    token,
    user,
    appName,
    landingPageEnabled,
    debugWidgetEnabled,
    authEnabled,
    credentialsAuthEnabled,
    keycloakAuthEnabled,
    oidcAutoRedirect,
    setupRequired,
    userRegistrationEnabled,
    emailVerificationEnabled,
    isAuthenticated,
    isAdmin,
    authType,
    login,
    logout,
    setTokens,
    fetchUserInfo,
    refreshUserInfo,
    checkAuthConfig,
    invalidateAuthConfig,
  }
})
